import React, { useEffect, useState } from 'react';
import { documentsAPI } from '../api/client';
import { extractErrorMessage } from '../utils/errors';

/**
 * Configure whether a policy must be attested to, and how often -- issue #64.
 *
 * Opened from DocumentViewPage.js, NOT from DocumentForm.js. DocumentForm is
 * draft-only, and switching attestation on for an already-published policy is
 * the normal case -- routing this through a revision would mean you could never
 * start collecting signatures on a live document. The backend agrees: the
 * requirement endpoints are gated on documents.update but not on DRAFT status,
 * and the requirement is deliberately absent from revision_service.SCALAR_FIELDS.
 *
 * The audience is NOT edited here. It is derived from the document's own
 * applicability tagging (owning location + applicability groups) against each
 * user's profile default locations, which is the whole point of the design --
 * a policy already declares where it applies. audience_size is shown instead,
 * because zero is the case that matters: a policy nobody is homed under will
 * read as 100% complete forever, and this is where an administrator finds out.
 */
function AcknowledgementSettingsModal({ documentId, onSaved, onClose }) {
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(false);
  const [recurs, setRecurs] = useState(false);
  const [frequencyYears, setFrequencyYears] = useState('1');
  const [instructions, setInstructions] = useState('');
  const [audienceSize, setAudienceSize] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    documentsAPI.getAcknowledgementRequirement(documentId)
      .then((res) => {
        const data = res.data;
        if (data) {
          setEnabled(data.is_active);
          setRecurs(data.frequency_years != null);
          if (data.frequency_years != null) setFrequencyYears(String(data.frequency_years));
          setInstructions(data.instructions || '');
          setAudienceSize(data.audience_size || 0);
        }
      })
      .catch((err) => setError(extractErrorMessage(err)))
      .finally(() => setLoading(false));
  }, [documentId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const res = await documentsAPI.saveAcknowledgementRequirement(documentId, {
        is_active: enabled,
        frequency_years: recurs ? Number(frequencyYears) : null,
        instructions: instructions.trim() || null,
      });
      setAudienceSize(res.data.audience_size || 0);
      if (onSaved) onSaved(res.data);
      onClose();
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <h3>Acknowledgement Requirement</h3>
        </div>

        {loading ? (
          <div className="loading">Loading...</div>
        ) : (
          <form onSubmit={handleSubmit}>
            {error && <div className="alert alert-error">{error}</div>}

            <div className="form-group">
              <label style={{ display: 'block', fontWeight: 'normal' }}>
                <input
                  type="checkbox"
                  checked={enabled}
                  onChange={(e) => setEnabled(e.target.checked)}
                  disabled={submitting}
                />{' '}
                <span>Staff must acknowledge this policy</span>
              </label>
              <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '6px' }}>
                Who is asked follows the policy's own location and applicability
                groups, matched against each person's default locations.
              </div>
            </div>

            <div className="form-group">
              <label style={{ display: 'block', fontWeight: 'normal' }}>
                <input
                  type="checkbox"
                  checked={recurs}
                  onChange={(e) => setRecurs(e.target.checked)}
                  disabled={submitting || !enabled}
                />{' '}
                <span>Require re-acknowledgement on a schedule</span>
              </label>
              {recurs && (
                <div className="acknowledgement-settings__years">
                  <label>Every</label>
                  <input
                    type="number"
                    min="1"
                    value={frequencyYears}
                    onChange={(e) => setFrequencyYears(e.target.value)}
                    disabled={submitting || !enabled}
                  />
                  <label>year(s)</label>
                </div>
              )}
              <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '6px' }}>
                Publishing a new version always asks everyone again, whether or
                not a schedule is set.
              </div>
            </div>

            <div className="form-group">
              <label>Statement</label>
              <textarea
                rows={3}
                value={instructions}
                onChange={(e) => setInstructions(e.target.value)}
                placeholder="Leave blank to use the standard wording."
                disabled={submitting || !enabled}
              />
              <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '6px' }}>
                What each person confirms. The wording in force when somebody
                signs is stored with their acknowledgement, so editing it later
                never rewrites past records.
              </div>
            </div>

            <div
              className={audienceSize === 0 ? 'alert alert-warning' : undefined}
              style={audienceSize === 0
                ? undefined
                : { fontSize: '12px', color: 'var(--muted)', marginTop: '6px' }}
            >
              {audienceSize === 0
                ? 'No one is currently required: nobody has a default location '
                  + "under this policy's locations, so it would report as complete "
                  + 'with zero signatures.'
                : `${audienceSize} person(s) currently required.`}
            </div>

            <div className="modal-footer">
              <button type="button" className="btn btn-secondary"
                      onClick={onClose} disabled={submitting}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={submitting}>
                {submitting ? 'Saving...' : 'Save'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default AcknowledgementSettingsModal;
